import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Sparkles, MessageSquare, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/* ================================================================
   Atlas — meta-orchestrator orb widget (Sprint I-4)
   Persistent across every /app/* route via AppLayout.
   ================================================================ */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AtlasOrbWidget() {
    const [open, setOpen]                 = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages]         = useState([]);
    const [quickPrompts, setQuickPrompts] = useState([]);
    const [input, setInput]               = useState("");
    const [streaming, setStreaming]       = useState(false);
    const [streamBuf, setStreamBuf]       = useState("");
    const feedRef = useRef(null);

    useEffect(() => {
        api.get("/atlas/quick-prompts").then((r) => setQuickPrompts(r.data.prompts || [])).catch(() => {});
        api.get("/atlas/conversation").then((r) => {
            const c = r.data?.conversation;
            if (c?.id) {
                setConversationId(c.id);
                setMessages(c.messages || []);
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }, [messages, streamBuf, open]);

    const send = async (text) => {
        const content = (text ?? input).trim();
        if (!content || streaming) return;
        setInput("");
        const now = new Date().toISOString();
        setMessages((m) => [...m, { role: "user", content, timestamp: now }]);
        setStreaming(true);
        setStreamBuf("");

        try {
            const token = localStorage.getItem("wfos_token");
            const resp = await fetch(`${API_BASE}/atlas/message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ content, conversation_id: conversationId }),
            });
            if (!resp.ok || !resp.body) {
                const j = await resp.json().catch(() => ({}));
                toast.error(j?.detail || "Atlas failed to respond.");
                setStreaming(false);
                return;
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let finalText = "";
            let convId = conversationId;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop() || "";
                for (const raw of parts) {
                    const lines = raw.split("\n");
                    let event = "message", data = "";
                    for (const l of lines) {
                        if (l.startsWith("event:")) event = l.slice(6).trim();
                        else if (l.startsWith("data:")) data += l.slice(5).trim();
                    }
                    if (!data) continue;
                    let obj = {};
                    try { obj = JSON.parse(data); } catch { continue; }
                    if (event === "start" && obj.conversation_id) {
                        convId = obj.conversation_id;
                        setConversationId(convId);
                    } else if (event === "token" && obj.delta) {
                        finalText += obj.delta;
                        setStreamBuf(finalText);
                    } else if (event === "done") {
                        setMessages((m) => [...m, { role: "assistant", content: obj.content || finalText, timestamp: new Date().toISOString() }]);
                        setStreamBuf("");
                    } else if (event === "error") {
                        toast.error(obj.error || "Atlas failed mid-stream.");
                    }
                }
            }
        } catch (e) {
            toast.error("Atlas stream interrupted.");
        } finally {
            setStreaming(false);
        }
    };

    return (
        <>
            {/* Orb trigger — fixed bottom-right (above the status bar).
                The outer conic-gradient ring spins via CSS; the label stays
                upright so users always read "Atlas" instead of a rotated
                phantom. */}
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="atlas-orb-trigger"
                aria-label="Open Atlas"
                title="Atlas — the meta-orchestrator"
                className="fixed z-40 bottom-14 right-6 rounded-full grid place-items-center group overflow-hidden"
                style={{
                    width: 62,
                    height: 62,
                    boxShadow:
                        "0 0 30px rgba(139, 92, 246, 0.5), 0 0 60px rgba(139, 92, 246, 0.3), inset 0 0 20px rgba(255,255,255,0.15)",
                }}
            >
                {/* Spinning gradient layer (independent of text) */}
                <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: "conic-gradient(from 0deg, #a78bfa, #8b5cf6, #6366f1, #06b6d4, #8b5cf6, #a78bfa)",
                        animation: "atlas-orb-spin 14s linear infinite",
                    }}
                />
                {/* Inner glow (static) */}
                <span
                    aria-hidden="true"
                    className="absolute inset-2 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.55), transparent 55%), radial-gradient(circle at 60% 70%, rgba(167,139,250,0.55), transparent 60%)",
                        filter: "blur(3px)",
                    }}
                />
                {/* Static label — always right-side-up */}
                <span
                    className="relative text-white text-[10.5px] font-bold tracking-[0.16em] uppercase select-none"
                    style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
                >
                    Atlas
                </span>
            </button>

            {/* Chat panel — slide in from right */}
            <AnimatePresence>
                {open && (
                    <motion.aside
                        key="panel"
                        initial={{ x: 24, opacity: 0 }}
                        animate={{ x: 0,  opacity: 1 }}
                        exit={{    x: 24, opacity: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut" }}
                        data-testid="atlas-orb-panel"
                        className="fixed z-40 bottom-24 right-6 w-[380px] max-w-[calc(100vw-40px)] rounded-2xl flex flex-col overflow-hidden atlas-card"
                        style={{
                            height: 540,
                            background: "linear-gradient(180deg, rgba(22, 22, 45, 0.92) 0%, rgba(14, 14, 32, 0.94) 100%)",
                            border: "1px solid rgba(139, 92, 246, 0.32)",
                            boxShadow: "0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(139,92,246,0.12), 0 0 80px -12px rgba(139,92,246,0.35)",
                            backdropFilter: "blur(24px)",
                            WebkitBackdropFilter: "blur(24px)",
                        }}
                    >
                        {/* Header */}
                        <div
                            className="flex items-center gap-3 px-4 py-3 border-b"
                            style={{ borderColor: "rgba(139,92,246,0.2)" }}
                        >
                            <span
                                className="w-8 h-8 rounded-full grid place-items-center"
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, #a78bfa, #8b5cf6, #06b6d4, #a78bfa)",
                                    boxShadow: "0 0 12px rgba(139,92,246,0.6)",
                                    animation: "atlas-orb-spin 10s linear infinite",
                                }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-[13.5px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                                    Atlas
                                </div>
                                <div className="text-[10.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                                    Meta-orchestrator · delegates to your Workforce
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="atlas-orb-close"
                                className="w-7 h-7 rounded-md grid place-items-center atlas-btn-ghost"
                                aria-label="Close"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        </div>

                        {/* Message feed */}
                        <div
                            ref={feedRef}
                            className="flex-1 overflow-y-auto atlas-scrollbar px-4 py-4 space-y-3"
                            data-testid="atlas-feed"
                        >
                            {messages.length === 0 && !streamBuf && (
                                <div className="text-center py-6">
                                    <div className="atlas-hero-eyebrow inline-flex">
                                        <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                                        Atlas is online
                                    </div>
                                    <div
                                        className="mt-4 text-[13.5px] leading-relaxed"
                                        style={{ color: "var(--atlas-text-secondary)" }}
                                    >
                                        I coordinate your Digital Workforce. Ask me who to route a task to, or ping me for a briefing.
                                    </div>
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <AtlasBubble key={i} role={m.role} content={m.content} />
                            ))}
                            {streamBuf && <AtlasBubble role="assistant" content={streamBuf} streaming />}
                        </div>

                        {/* Quick prompts + composer */}
                        <div className="px-3 pb-3 pt-2 border-t" style={{ borderColor: "rgba(139,92,246,0.2)" }}>
                            {quickPrompts.length > 0 && messages.length === 0 && (
                                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="atlas-quick-prompts">
                                    {quickPrompts.map((qp) => (
                                        <button
                                            key={qp.key}
                                            onClick={() => send(qp.prompt)}
                                            disabled={streaming}
                                            data-testid={`atlas-quick-${qp.key}`}
                                            className="atlas-btn-ghost rounded-full px-3 h-7 text-[11px] font-medium disabled:opacity-50"
                                        >
                                            {qp.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <form
                                onSubmit={(e) => { e.preventDefault(); send(); }}
                                className="flex items-center gap-2 rounded-xl px-3 py-2"
                                style={{
                                    background: "var(--atlas-bg-elevated)",
                                    border: "1px solid var(--atlas-border-strong)",
                                }}
                            >
                                <MessageSquare
                                    className="w-3.5 h-3.5"
                                    strokeWidth={2}
                                    style={{ color: "var(--atlas-text-muted)" }}
                                />
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={streaming}
                                    placeholder="Ask Atlas anything…"
                                    data-testid="atlas-composer-input"
                                    className="flex-1 bg-transparent outline-none text-[13px]"
                                    style={{ color: "var(--atlas-text-primary)" }}
                                />
                                <button
                                    type="submit"
                                    disabled={!input.trim() || streaming}
                                    data-testid="atlas-composer-send"
                                    className="atlas-btn-primary inline-flex items-center gap-1 rounded-md px-2.5 h-7 text-[11.5px] disabled:opacity-50"
                                >
                                    {streaming ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} /> : <Send className="w-3 h-3" strokeWidth={2} />}
                                </button>
                            </form>
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>
        </>
    );
}

function AtlasBubble({ role, content, streaming }) {
    const isUser = role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${streaming ? "atlas-caret" : ""}`}
                style={
                    isUser
                        ? {
                              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                              color: "#fff",
                              boxShadow: "0 4px 16px -4px rgba(139,92,246,0.45)",
                          }
                        : {
                              background: "var(--atlas-bg-highlight)",
                              color: "var(--atlas-text-primary)",
                              border: "1px solid var(--atlas-border-strong)",
                          }
                }
            >
                {content}
            </div>
        </div>
    );
}
